// eslint-disable-next-line import/extensions -- temp fix
import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from '@ori-ui/mui';
import useTestId from '@ori/testid-generator';
import type { FC } from 'react';
import React, { useCallback, useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import { useCreateNewsletterSubscription } from '../../../../api/createNewsletterSubscription';
import { PrivacyPolicy } from '../PrivacyPolicy';
import { TitleIcon } from '../TitleIcon';
import { useNewsletterDialogContext } from './components';
import { EmailInput } from './components/EmailInput';
import { NEWSLETTER_DIALOG, NEWSLETTER_DIALOG_SUBMIT, transitionProps } from './constants';
import { Content, Root } from './styles';
import type { NewsletterDialogForm, NewsletterDialogProps } from './types';
import { SubscriptionProcessStatus } from './types';
import { getColorFromStatus, useValidationSchema } from './utils';

export const NewsletterDialog: FC<NewsletterDialogProps> = ({ open, origin, onClose }) => {
  const { getTestId } = useTestId();
  const { content, setStatus, status } = useNewsletterDialogContext();
  const { createSubscription } = useCreateNewsletterSubscription();
  const { newsletterDialogValidationSchema } = useValidationSchema();
  const form = useForm<NewsletterDialogForm>({
    mode: 'onChange',
    defaultValues: { email: '' },
    resolver: zodResolver(newsletterDialogValidationSchema),
  });
  const shouldDisableSubmit =
    status === SubscriptionProcessStatus.Initial && (!!form.formState.errors.email || form.getValues('email') === '');

  const onSubscribe = useCallback(
    async ({ email }: NewsletterDialogForm) => {
      await createSubscription({ email, origin });
    },
    [createSubscription, origin],
  );

  const handleClose = useCallback(() => {
    onClose();
    setStatus(SubscriptionProcessStatus.Initial);
    form.reset({ email: '' });
  }, [onClose, setStatus, form]);

  const onSubsribeOrTryAgain = useCallback(async () => {
    if (status === SubscriptionProcessStatus.Failed) {
      setStatus(SubscriptionProcessStatus.Initial);
    } else {
      await form.handleSubmit(onSubscribe)();
    }
  }, [form, onSubscribe, setStatus, status]);

  const actions = useMemo(
    () =>
      content.showButton
        ? [
            <Button
              key="button"
              fullWidth
              data-testid={getTestId(NEWSLETTER_DIALOG_SUBMIT)}
              variant="contained"
              color="primary"
              disabled={shouldDisableSubmit}
              onClick={onSubsribeOrTryAgain}
            >
              {content.buttonText}
            </Button>,
          ]
        : undefined,

    [content.buttonText, content.showButton, getTestId, onSubsribeOrTryAgain, shouldDisableSubmit],
  );
  const icon = useMemo(() => <TitleIcon icon={content.icon} />, [content.icon]);

  return (
    <Root
      disablePortal={false}
      transitionProps={transitionProps}
      data-testid={getTestId(NEWSLETTER_DIALOG)}
      open={open}
      header={content.title}
      aria-labelledby={NEWSLETTER_DIALOG}
      actions={actions}
      icon={icon}
      color={getColorFromStatus(status)}
      onClose={handleClose}
    >
      <FormProvider {...form}>
        <Content>
          {content.text}
          {content.showForm ? <EmailInput /> : null}
          <PrivacyPolicy
            text={content.ppText}
            linkText={content.ppLinkText}
            url={content.ppUrl}
          />
        </Content>
      </FormProvider>
    </Root>
  );
};
